import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/list';
import { MatFormField, MatLabel, MatOption, MatSelect } from '@angular/material/select';
import { EnpsComponent } from '@common/components/base/enps-component';
import { UserStaffInfo } from '@common/data-models/service/user-staff-info';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { LanguageSettings } from '@common/services/language-settings';
import { GlobalRoles, InvitationStatusCodes } from '@common/utils/util';
import { Store } from '@ngrx/store';
import { AppService } from '@suite/app.service';

import { LoaderComponent } from '../../../../loader/loader';
import { TextFieldComponent } from '../../../controls/text-field/text-field';
import { TeamMembersActions } from '../../../store/team-members/team-members.actions';

@Component({
    selector: 'st-add-team-member',
    imports: [FormsModule, LoaderComponent, MatButton, MatButtonToggleGroup, MatDivider, MatFormField, MatLabel, MatOption, MatSelect, ReactiveFormsModule, TextFieldComponent],
    templateUrl: './add-team-member.html',
    styleUrl: './add-team-member.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-add-team-member',
    },
})
export class AddTeamMemberComponent extends EnpsComponent implements OnInit {
    appService = inject(AppService);
    private store = inject(Store);
    globalTablesService = inject(GlobalTablesService);
    languageSettings = inject(LanguageSettings);
    dialogRef = inject<MatDialogRef<AddTeamMemberComponent>>(MatDialogRef);
    inputData = inject(MAT_DIALOG_DATA);

    public InvitationStatusCodes: any = InvitationStatusCodes;
    internalProgramFormControl: FormControl = new FormControl(null);
    internalRoleFormControl: FormControl = new FormControl(null);
    internalStatusFormControl: FormControl = new FormControl(null);

    public readonly newUser = signal(new UserStaffInfo());
    public readonly newUserCurrentRole = signal(null);
    readonly loading = signal(false);

    readonly disabled = computed(() => {
        const user = this.newUser();
        return !user.UserID.trim() || !user.GivenName.trim() || !user.Surname.trim() || !user.Program.trim() || !user.Active || !this.newUserCurrentRole();
    });

    ngOnInit() {
        this.internalProgramFormControl.markAsTouched();
        this.internalRoleFormControl.markAsTouched();
        this.internalStatusFormControl.markAsTouched();
    }

    public newUserSelectRole() {
        const role = this.newUserCurrentRole();
        if (role) {
            this.newUser.update(user => {
                const updated = Object.assign(new UserStaffInfo(), user);
                if (role.ID === GlobalRoles.SUPERVISOR_ID) {
                    updated.Supervisor = true;
                } else {
                    updated.Supervisor = false;
                    updated.ServerManager = false;
                }
                updated.GlobalRole = role.ID;
                return updated;
            });
        }
    }

    public addUser() {
        if (!this.disabled()) {
            const user = this.newUser();
            const toCreate = Object.assign(new UserStaffInfo(), user);
            toCreate.UserID = toCreate.UserID.trim();
            toCreate.GivenName = toCreate.GivenName.trim();
            toCreate.Surname = toCreate.Surname.trim();
            toCreate.Email = toCreate.Email.trim();
            toCreate.SSOLogins = toCreate.SSOLogins.trim();
            this.store.dispatch(TeamMembersActions.createTeamMember({ user: toCreate }));
            this.dialogRef.close();
        }
    }

    public cancelNew() {
        this.dialogRef.close();
    }

    public onNewUserChanged(event: any, prop: string) {
        this.newUser.update(user => {
            const updated = Object.assign(new UserStaffInfo(), user);
            updated[prop] = event;
            return updated;
        });
    }

    public onRoleChanged(role: any) {
        this.newUserCurrentRole.set(role);
        this.newUserSelectRole();
    }
}
